import Papa from "papaparse";

export type CorrelationMatrixData = Record<string, Record<string, number | null>>;

export type CorrelationCsvEntity = { id: string; label: string };

// QP-164 — asset class names are restricted from containing ";" specifically
// so it's safe to use as the correlation CSV's delimiter.
const CSV_DELIMITER = ";";

const isInRange = (v: number): boolean => Number.isFinite(v) && v >= -1 && v <= 1;

export function buildCorrelationCsv(
  entities: CorrelationCsvEntity[],
  matrix: CorrelationMatrixData,
): string {
  const header = ["", ...entities.map((e) => e.label)];
  const rows = entities.map((rowEntity, rowIdx) => {
    const row = [rowEntity.label];
    entities.forEach((colEntity, colIdx) => {
      if (rowIdx === colIdx) {
        row.push("");
        return;
      }
      // The on-screen grid only ever writes the "higher display index -> lower
      // display index" direction of a pair (handleCorrelationChange is wired
      // to the lower-triangle cells only). Since QL-655 the API stores every
      // pair in both directions with one value, but a matrix saved before then
      // could hold a padded 0 in the other direction. Always read the canonical
      // (higher -> lower index) direction, exactly like the grid does, so CSV
      // export matches what's shown on screen.
      const [fromId, toId] =
        rowIdx > colIdx ? [rowEntity.id, colEntity.id] : [colEntity.id, rowEntity.id];
      const value = matrix[fromId]?.[toId] ?? null;
      row.push(value === null ? "" : value.toFixed(2));
    });
    return row;
  });
  return Papa.unparse([header, ...rows], { delimiter: CSV_DELIMITER });
}

export type CorrelationCsvParseResult =
  | { matrix: CorrelationMatrixData; error: null }
  | { matrix: null; error: string };

/** Parses a correlation CSV, validating every row/column label against `entities` and every value against [-1, 1]. Rejects the whole file on the first problem found. */
export function parseCorrelationCsv(
  text: string,
  entities: CorrelationCsvEntity[],
): CorrelationCsvParseResult {
  const { data, errors } = Papa.parse<string[]>(text, {
    delimiter: CSV_DELIMITER,
    skipEmptyLines: "greedy",
  });

  if (errors.length > 0) {
    const first = errors[0];
    return { matrix: null, error: `Line ${(first.row ?? 0) + 1}: ${first.message}` };
  }
  if (data.length < 2) {
    return { matrix: null, error: "CSV must have a header row and at least one data row" };
  }

  const labelToId = new Map(entities.map((e) => [e.label, e.id]));

  const [headerRow, ...dataRows] = data as [string[], ...string[][]];
  const columnIds: string[] = [];
  for (let c = 1; c < headerRow.length; c++) {
    const label = headerRow[c]?.trim() ?? "";
    const id = labelToId.get(label);
    if (!id) {
      return { matrix: null, error: `Unknown column "${label}" in header row` };
    }
    columnIds.push(id);
  }

  // Correlation is symmetric and the on-screen grid only ever edits/reads one
  // canonical direction per pair — so a pair's value is tracked per unordered
  // {a, b} key here, and written to BOTH directions of the output matrix.
  // A CSV that gives conflicting values for the two cells of the same pair
  // is rejected, consistent with "reject the whole file on any problem".
  const pairValues = new Map<string, { value: number; label: string }>();
  const seenRowIds = new Set<string>();

  for (let r = 0; r < dataRows.length; r++) {
    const row = dataRows[r]!;
    const rowLabel = row[0]?.trim() ?? "";
    const rowId = labelToId.get(rowLabel);
    if (!rowId) {
      return { matrix: null, error: `Line ${r + 2}: unknown row "${rowLabel}"` };
    }
    if (seenRowIds.has(rowId)) {
      return { matrix: null, error: `Line ${r + 2}: duplicate row "${rowLabel}"` };
    }
    seenRowIds.add(rowId);

    for (let c = 0; c < columnIds.length; c++) {
      const colId = columnIds[c]!;
      if (colId === rowId) continue;
      const raw = row[c + 1]?.trim() ?? "";
      if (raw === "") continue;
      const value = Number(raw);
      if (!isInRange(value)) {
        return {
          matrix: null,
          error: `Line ${r + 2}: value "${raw}" for "${rowLabel}" is not a number between -1 and 1`,
        };
      }

      const pairKey = [rowId, colId].sort().join("|");
      const existing = pairValues.get(pairKey);
      if (existing && existing.value !== value) {
        return {
          matrix: null,
          error: `Line ${r + 2}: "${rowLabel}" gives ${value} for a pair already set to ${existing.value} elsewhere in the file`,
        };
      }
      pairValues.set(pairKey, { value, label: rowLabel });
    }
  }

  const matrix: CorrelationMatrixData = {};
  for (const pairKey of pairValues.keys()) {
    const { value } = pairValues.get(pairKey)!;
    const [idA, idB] = pairKey.split("|") as [string, string];
    matrix[idA] = matrix[idA] ?? {};
    matrix[idA]![idB] = value;
    matrix[idB] = matrix[idB] ?? {};
    matrix[idB]![idA] = value;
  }

  return { matrix, error: null };
}

export function downloadCsv(csvText: string, filename: string): void {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
